/**
 * Role-based redirect utility for handling user navigation after login
 * 
 * Role mapping based on folder structure:
 * - Role 0 (Super Admin) → Admin Panel (/dashboard/admin) → src/admin/AdminLayout.jsx
 * - Role 1 (User) → User Panel (/dashboard/user) → src/user/UserLayout.jsx - Always, regardless of organization
 * - Role 2 (Org Admin) → Organization Admin Panel (/org-admin) → src/organization/Layout.jsx
 */

/**
 * Redirects user to the appropriate panel based on their role
 * @param {number|string} role - User role (0, 1, or 2)
 * @param {function} navigate - React Router navigate function
 * @param {object} options - Additional options
 * @param {boolean} options.replace - Whether to replace current history entry (default: true)
 * @returns {string} - The redirect path that was navigated to
 */
export const redirectByRole = (role, navigate, options = {}) => {
  const { replace = true } = options;
  const roleNum = parseInt(role);
  let redirectPath;

  switch (roleNum) {
    case 0: // Super Admin
      redirectPath = '/admin/dashboard';
      break;
    case 1: // User
      redirectPath = '/user/dashboard';
      break;
    case 2: // Organization Admin
      redirectPath = '/organization/dashboard';
      break;
    case 3: // Instructor
      redirectPath = '/instructor/dashboard';
      break;
    case 4: // Manager
      redirectPath = '/manager/dashboard';
      break;
    case 5: // Master Admin
      redirectPath = '/masteradmin/dashboard';
      break;
    default:
      // Custom roles (created in Roles & permissions, values above 5) use the
      // Instructor panel, which shows only the screens the role is allowed.
      if (roleNum > 5) {
        redirectPath = '/instructor/dashboard';
        break;
      }
      console.warn(`Unknown role: ${role}. Redirecting to home page.`);
      redirectPath = '/';
      break;
  }

  navigate(redirectPath, { replace });
  return redirectPath;
};

export const getDefaultRouteForRole = (role) => {
  const roleNum = parseInt(role);
  switch (roleNum) {
    case 0: return '/admin/dashboard';
    case 1: return '/user/dashboard';
    case 2: return '/organization/dashboard';
    case 3: return '/instructor/dashboard';
    case 4: return '/manager/dashboard';
    case 5: return '/masteradmin/dashboard';
    default: return roleNum > 5 ? '/instructor/dashboard' : '/';
  }
};

export const hasRouteAccess = (userRole, routePath) => {
  const roleNum = parseInt(userRole);

  if (roleNum === 0) return true; // Super Admin — all routes
  if (roleNum === 1) return routePath.startsWith('/user') || routePath === '/';
  if (roleNum === 2) return routePath.startsWith('/organization') || routePath === '/';
  if (roleNum === 3) return routePath.startsWith('/instructor') || routePath === '/';
  if (roleNum === 4) return routePath.startsWith('/manager') || routePath === '/';
  if (roleNum === 5) return routePath.startsWith('/masteradmin') || routePath === '/';
  if (roleNum > 5) return routePath.startsWith('/instructor') || routePath === '/'; // custom roles

  return false;
};

export const getRoleName = (role) => {
  const roleNum = parseInt(role);
  switch (roleNum) {
    case 0: return 'Super Admin';
    case 1: return 'User';
    case 2: return 'Organization Admin';
    case 3: return 'Instructor';
    case 4: return 'Manager';
    case 5: return 'Master Admin';
    default: return 'Unknown Role';
  }
};